import { AxiosInstance } from 'axios'
import {
    CustomFieldValue,
    CustomFieldValueBulkCreate,
    CustomFieldValueBulkUpsert,
    CustomFieldValueCreate,
    ListCustomFieldValuesParams,
    PaginatedCustomFieldValueResponse,
} from '@workspace/lib/api'

export class CustomFieldValueApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all custom field values with pagination
     */
    async list(params?: ListCustomFieldValuesParams): Promise<PaginatedCustomFieldValueResponse> {
        const response = await this.client.get<PaginatedCustomFieldValueResponse>('/custom-field-values', {
            params,
        })
        return response.data
    }

    /**
     * Get custom field values by entity type and ID
     */
    async getByEntity(entityType: string, entityId: string): Promise<PaginatedCustomFieldValueResponse> {
        return this.list({ entityType, entityId })
    }

    /**
     * List custom field values for a specific project (new project-scoped endpoint)
     */
    async listByProject(
        projectId: string,
        params?: ListCustomFieldValuesParams,
    ): Promise<PaginatedCustomFieldValueResponse> {
        const response = await this.client.get<PaginatedCustomFieldValueResponse>(
            `/projects/${projectId}/custom-field-values`,
            {
                params,
            },
        )
        return response.data
    }

    /**
     * Get a single custom field value by ID
     */
    async findOne(id: string): Promise<CustomFieldValue> {
        const response = await this.client.get<CustomFieldValue>(`/custom-field-values/${id}`)
        return response.data
    }

    /**
     * Create a new custom field value
     */
    async create(payload: CustomFieldValueCreate): Promise<CustomFieldValue> {
        const response = await this.client.post<CustomFieldValue>('/custom-field-values', payload)
        return response.data
    }

    /**
     * Bulk create custom field values
     */
    async bulkCreate(payload: CustomFieldValueBulkCreate): Promise<CustomFieldValue[]> {
        const response = await this.client.post<CustomFieldValue[]>('/custom-field-values', payload)
        return response.data
    }

    /**
     * Bulk upsert (create or update) custom field values
     */
    async bulkUpsert(payload: CustomFieldValueBulkUpsert): Promise<CustomFieldValue[]> {
        const response = await this.client.put<CustomFieldValue[]>('/custom-field-values', payload)
        return response.data
    }

    /**
     * Delete a custom field value
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/custom-field-values/${id}`)
    }

    /**
     * Delete custom field values by entity
     */
    async deleteByEntity(entityType: string, entityId: string): Promise<void> {
        await this.client.delete('/custom-field-values', { data: { entityType, entityId } })
    }
}
